import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {SagaIterator} from 'redux-saga';
import {call, put, select, takeEvery, cancel, all} from 'redux-saga/effects';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PAYMENT_CODE_MESSAGE, STEPS} from 'desktop/pages/checkout/constants/checkout';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';

import {ActionType} from 'types/reducers';

import {setDefaultStateForCompleteOrder} from '../../../actions/completeOrder/completeOrder';
import {eventAmazonAuth} from '../../../reducers/paymentStep/amazonV2/amazonV2';
import {sendPayerInfoToSaga} from '../../../reducers/paymentStep/payerInfo/payerInfo';
import {changePaymentMethod as changePaymentMethodAction} from '../../../reducers/paymentStep/paymentMethods/paymentMethods';
import {
    cleanError,
    savePayment,
} from '../../../reducers/paymentStep/savePaymentData/savePaymentData';
import {setSelectedMethod} from '../../../reducers/paymentStep/selectedMethod/selectedMethod';
import {getStepStatus} from '../../../selectors/currentStep/currentStep';
import {
    getMessageError,
    getNameSelectedMethod,
    getPaymentStepIsCompleted,
    getSelectedMethod,
    getStatusOfLoadingSaveOrder,
} from '../../../selectors/paymentStep/paymentStep';
import {saveAndSetPaymentMethod} from '../payerData/payerData';

import {setLoadingSavePaymentSaga} from '../setLoadingSavePaymentSaga/setLoadingSavePaymentSaga';

import {scrollToInPaymentStep} from '../utils/scrollToInPaymentStep';

import {initialAmazon} from './amazon/amazon/amazonV2';

/**
 * Clear error message
 */
export function* clearMessage(): SagaIterator {
    const messageError = yield select(getMessageError);

    if (messageError !== '') {
        yield put(cleanError());
    }
}

/**
 * Call initial methods for amazon or paypal
 */
export function* changePaymentMethod(): SagaIterator {
    const name = yield select(getNameSelectedMethod);
    const selectedMethod = yield select(getSelectedMethod);

    if (selectedMethod) {
        if (name === PAYMENTS.METHOD_AMAZON_V2) yield call(initialAmazon);
    }

    yield call(clearMessage);
}

/**
 * Get orider totals, cmpleted step, stop loadings before completed step
 */
function* beforeStepCompleted(): SagaIterator {
    yield call(setLoadingSavePaymentSaga, false);
}

/**
 * if payment is completed then call amazonAuth else call initialAmazon
 * only if payment method selected amazon
 */
export function* callAmazonV2EventAuthOrInit(stepStatusCompleted: boolean) {
    if (stepStatusCompleted) {
        yield put(eventAmazonAuth());
    } else {
        yield call(initialAmazon);
    }
}

/**
 * if payment method selected amazon
 * if amazon V2 call callAmazonV2EventAuthOrInit else callAmazonEventAuthOrInit
 */
export function* beforeInitialAmazon(): SagaIterator {
    const name = yield select(getNameSelectedMethod);
    const selectedMethod = yield select(getSelectedMethod);
    const stepStatus = yield select(getStepStatus, STEPS.PAYMENT_STEP);

    if (name !== PAYMENTS.METHOD_AMAZON_V2 || !selectedMethod) yield cancel();

    yield call(callAmazonV2EventAuthOrInit, stepStatus.completed);
}

/**
 *  Scroll to payment method if error
 */
export function* processingErrorsPaymentStep(): SagaIterator {
    const name = yield select(getNameSelectedMethod);

    yield call(scrollToInPaymentStep, name);
    yield call(setLoadingSavePaymentSaga, false);
}

/**
 * Change Payment Method
 */
export function* changePaymentMethodSaga({payload: code}: ActionType<PAYMENTS>): SagaIterator {
    const [isSaveLoading, isStepCompleted, selectedMethod] = yield all([
        select(getStatusOfLoadingSaveOrder),
        select(getPaymentStepIsCompleted),
        select(getSelectedMethod),
    ]);

    if (selectedMethod !== code && !isSaveLoading && !isStepCompleted) {
        yield put(setSelectedMethod(code));
        yield put(
            setDefaultStateForCompleteOrder({
                [STEPS.PAYMENT_STEP]: {},
            }),
        );

        if (code in PAYMENT_CODE_MESSAGE) {
            yield call(sendDesktopAnalyticsEvent, CHECKOUT_PAYMENT_STEP, {
                message: `Radio Button Click - ${PAYMENT_CODE_MESSAGE[code]}`,
                method: 'Payment',
            });
        }
    }
}

/**
 * Begin of saga
 */
export function* paymentMethodsSaga(): SagaIterator {
    yield takeEvery(`${sendPayerInfoToSaga}`, saveAndSetPaymentMethod);
    yield takeEvery(`${setSelectedMethod}`, changePaymentMethod);
    yield takeEvery(`${savePayment.fulfilled}`, beforeStepCompleted);
    yield takeEvery(`${changePaymentMethodAction}`, changePaymentMethodSaga);
    yield takeEvery(`${savePayment.rejected}`, processingErrorsPaymentStep);
}
